/**
 * LIGHT CARDS — the examiner's card, and the three questions he asks after it.
 *
 * Founder, 2026-08-12: "cards are shown of lights and second mates and masters are asked to explain
 * them, and also quote the rules … they show many times lights which can be showing more than one
 * rule and so what actions to take, this is generally tricky."
 *
 * ── BUILT ON THE GLASS SYSTEM, AND THE FIRST VERSION WAS NOT ───────────────────────────────────
 * The first cut of this file hand-rolled its layout in inline styles — a grid, a blockquote with a
 * border I invented — while the rest of the surface composes `g-glass shell-panel` panels and renders
 * facts through <Value>. Founder: "we agreed on glass finish and world class ui". That drift is
 * exactly what the one-surface ruling exists to stop, so this is rebuilt on the primitives.
 *
 * THE RULE QUOTE IS A CITATION, NOT A BLOCKQUOTE. Rendering it through Value/cited() is not cosmetic:
 * it puts the quote in the same provenance state as every other quoted fact in the product, so a
 * candidate sees the SAME visual grammar for "this is quoted from a source" here as in a past paper.
 * A blockquote I styled myself would have said "trust me" in a product whose whole claim is
 * compute-quote-or-null.
 *
 * ── THE CARD IS A QUESTION, SO THE ANSWER IS NOT IN THE PAGE UNTIL IT IS ASKED FOR ─────────────
 * /api/lightcards carries picture and aspect only; identification, quoted rule, confusions and action
 * come from /api/lightcard?id= on reveal. A view holding the answer in memory eventually leaks it
 * into the DOM, and a candidate who can inspect the answer has stopped practising.
 *
 * AND THE CONFUSIONS COME AFTER THE ANSWER, NOT BESIDE IT. What separates a pass from a fail on this
 * board is not naming the lights — it is knowing which OTHER card this one can be taken for and which
 * single light tells them apart. The reveal reads in the order an examiner walks it: what she is · the
 * rule quoted · what it can be mistaken for, with the discriminator · what you do, with its own rule.
 */
import { useEffect, useState, useCallback } from 'react';
import { Value } from '../../../packages/ankr-glass/src/react.jsx';
import { cited, refused, failed } from '../../../packages/ankr-provenance/src/provenance.mjs';
import { API } from './surfaces.jsx';

export default function LightCards() {
  const [cards, setCards] = useState(null);
  const [i, setI] = useState(0);
  const [full, setFull] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    let live = true;
    fetch(`${API}/api/lightcards`)
      .then((r) => r.json())
      .then((d) => { if (live) setCards(d.cards || []); })
      .catch(() => { if (live) setErr('the deck could not be loaded'); });
    return () => { live = false; };
  }, []);

  const card = cards && cards[i];
  const reveal = useCallback(() => {
    if (!card) return;
    fetch(`${API}/api/lightcard?id=${encodeURIComponent(card.id)}`)
      .then((r) => r.json()).then(setFull)
      .catch(() => setErr('the answer could not be loaded'));
  }, [card]);

  const go = (d) => { setFull(null); setI((n) => (n + d + cards.length) % cards.length); };

  if (err) return <div className="g-glass shell-panel gl-empty g-body"><Value of={failed(err)} /></div>;
  if (!cards) return <div className="g-glass shell-panel gl-empty g-body">Loading the deck…</div>;
  if (!cards.length) {
    return <div className="g-glass shell-panel gl-empty g-body">
      <Value of={refused('no light cards are shelved yet')} /></div>;
  }

  return (
    <div className="lc-wrap">
      <div className="g-glass shell-panel">
        <div className="lc-head">
          <span className="g-h2">{card.id}</span>
          <span className="g-dim">you are {String(card.aspect || '').replace(/_/g, ' ')} of her</span>
          <span className="g-dim lc-count">{i + 1} of {cards.length}</span>
        </div>
        {/* The server's computed SVG, inlined from the list response — so the deck works offline with
            the rest of the study material instead of failing at sea on a second request. */}
        <div className="lc-card" dangerouslySetInnerHTML={{ __html: card.svg || '' }} />
        <div className="lc-actions">
          <button className="g-btn g-btn-primary" onClick={reveal} disabled={!!full}>
            {full ? 'Answer shown' : 'What is she — and which rule?'}
          </button>
          <button className="g-btn" onClick={() => go(-1)}>Previous</button>
          <button className="g-btn" onClick={() => go(1)}>Next card</button>
        </div>
      </div>

      {full && (
        <>
          <div className="g-glass shell-panel">
            <h3 className="g-h2">What she is</h3>
            <p className="g-body">{full.answer}</p>
          </div>

          <div className="g-glass shell-panel">
            <h3 className="g-h2">The rule</h3>
            <Value of={cited(full.quote, (full.rules || []).join(' · '))} />
          </div>

          {!!(full.confusable_with || []).length && (
            <div className="g-glass shell-panel">
              <h3 className="g-h2">What it can be mistaken for</h3>
              <p className="g-dim g-body">This is the part that is examined — not the naming.</p>
              {full.confusable_with.map((c, n) => (
                <div key={n} className="lc-conf">
                  <span className="g-chip">{c.with}</span>
                  <p className="g-body">{c.why}</p>
                  <p className="g-body lc-disc"><strong>How you tell:</strong> {c.discriminator}</p>
                </div>
              ))}
            </div>
          )}

          <div className="g-glass shell-panel">
            <h3 className="g-h2">What you do</h3>
            <Value of={cited(full.action, (full.action_rules || []).join(' · '))} />
          </div>
        </>
      )}
    </div>
  );
}
